import Step1 from './step1/Step1';
import Step2 from './step2/Step2';
import Step3 from './step3/Step3';
import Step4 from './step4/Step4';
import WaitlistScreen from './waitlist/WaitlistScreen';
import TimeLimit from './components/TimeLimit.js';
import IconClose from './icons/IconClose.jsx';
// import LanguagePicker from './components/LanguagePicker.js';
// import LanguagePickerSimple from './components/LanguagePickerSimple.js';
import { useGetShow } from '../openapi/shows/shows.js';
import { CartContextProvider, useCart } from './hooks/useCart.js';
import { useGetTicketTypes } from '../openapi/ticket-types/ticket-types.js';
import { useGetTicketOptions } from '../openapi/ticket-options/ticket-options.js';
import ReservationDetails from './reservation/ReservationDetails';
// import { LANGUAGE_OPTIONS } from './hooks/useLanguage.js';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isGenericFireknifeDomain } from './helpers/widgetSlugDomain.js';
import { isStep3Needed, isCartOnlyFullAuto } from './helpers/stepNavigation.js';
import useSmartStorage from './hooks/useSmartStorage.js';
import { useWaitlist } from './hooks/useWaitlist.js';
import { IS_DEVELOPMENT_ENV } from '../config.js';
import { LocalStorageKeys } from './common/LocalStorageKeys.js';
import { useMarketing } from './hooks/useMarketing.tsx';
import { useShowStyling } from './helpers/showStyling';

function StepRouter({ step, setStep, show, showId, setReservationUrlParams }) {
    const { cart } = useCart();
    const { data: { ticketTypes } = { ticketTypes: [] } } = useGetTicketTypes({
        optionalId: showId,
        includeSeats: true,
        onlyShowInList: true,
        onlyGuestVisible: true,
    });
    const { data: { ticketOptions } = { ticketOptions: [] } } = useGetTicketOptions({
        optionalId: showId,
        onlyGuestVisible: true,
    });
    const [autoSeatingSkipStep3, setAutoSeatingSkipStep3] = useState(false);
    const { waitlistState, openWaitlist, closeWaitlist, resetBookingState } = useWaitlist({
        ticketTypes,
        ticketOptions,
        setStep,
    });

    const delayedSeatAssignmentEnabled = !!show?.marketing?.delayedSeatAssignmentEnabled;
    const stepNeedsStep3 = isStep3Needed(cart, ticketTypes, delayedSeatAssignmentEnabled);
    const effectiveAutoSeatingSkipStep3 =
        autoSeatingSkipStep3 || isCartOnlyFullAuto(cart, ticketTypes);

    const handleSetStep = (newStep) => {
        console.log(`Setting step from ${step} to ${newStep} stepNeedsStep3: ${stepNeedsStep3}`);
        // If trying to go to step 3 but it's not needed, skip to step 4
        if (newStep === 3 && step === 2 && !stepNeedsStep3) {
            setStep(4);
            return;
        }

        // If coming from step 4 back to step 3 but step 3 is not needed or was auto-skipped for fullauto, go to step 2
        if (newStep === 3 && step === 4 && (!stepNeedsStep3 || effectiveAutoSeatingSkipStep3)) {
            setStep(2);
            return;
        }

        setStep(newStep);
    };

    if (waitlistState.isActive) {
        return (
            <WaitlistScreen
                show={show}
                forceSoldOutStatus={waitlistState.forceSoldOutStatus}
                onBackToTickets={() => {
                    closeWaitlist();
                    setStep(2);
                }}
                onViewMoreDates={() => {
                    closeWaitlist();
                    resetBookingState();
                    setStep(1);
                }}
            />
        );
    }

    return (
        <>
            {step === 1 && (
                <Step1
                    setStep={handleSetStep}
                    show={show}
                    showId={showId}
                    delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                />
            )}
            {step === 2 && (
                <Step2
                    setStep={handleSetStep}
                    show={show}
                    stepNeedsStep3={stepNeedsStep3}
                    delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                    onOpenWaitlist={() => openWaitlist(false)}
                />
            )}
            {step === 3 && stepNeedsStep3 && (
                <Step3
                    setStep={handleSetStep}
                    show={show}
                    setAutoSeatingSkipStep3={setAutoSeatingSkipStep3}
                    delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                />
            )}
            {step === 4 && (
                <Step4
                    setStep={handleSetStep}
                    show={show}
                    setReservationUrlParams={setReservationUrlParams}
                    stepNeedsStep3={stepNeedsStep3}
                    autoSeatingSkipStep3={effectiveAutoSeatingSkipStep3}
                    delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                    onOpenWaitlist={() => openWaitlist(true)}
                />
            )}
        </>
    );
}

const getReservationParamsFromUrl = () => {
    const params = new URLSearchParams(globalThis.location.search);
    return { rid: params.get('rid'), rhash: params.get('rhash') };
};

const useReservationUrlParams = (): {
    reservationId: number | null;
    reservationHash: string | null;
    setReservationUrlParams: (reservationId: number, reservationHash: string) => void;
    clearReservationUrlParams: () => void;
} => {
    const [urlParams, setUrlParams] = useState(getReservationParamsFromUrl);

    const setReservationUrlParams = (reservationId: number, reservationHash: string) => {
        const newParams = new URLSearchParams(globalThis.location.search);
        newParams.set('rid', `${reservationId}`);
        newParams.set('rhash', reservationHash);
        const q = newParams.toString();
        globalThis.history.replaceState(
            {},
            '',
            q ? `${globalThis.location.pathname}?${q}` : globalThis.location.pathname,
        );
        setUrlParams({ rid: `${reservationId}`, rhash: reservationHash });
    };

    const clearReservationUrlParams = () => {
        const newParams = new URLSearchParams(globalThis.location.search);
        newParams.delete('rid');
        newParams.delete('rhash');
        const q = newParams.toString();
        globalThis.history.replaceState(
            {},
            '',
            q ? `${globalThis.location.pathname}?${q}` : globalThis.location.pathname,
        );
        setUrlParams({ rid: null, rhash: null });
    };

    const reservationId = urlParams.rid ? Number.parseInt(urlParams.rid, 10) : null;
    return {
        reservationId,
        reservationHash: urlParams.rhash,
        setReservationUrlParams,
        clearReservationUrlParams,
    };
};

export default function AppWidget({
    showId,
    setWidgetOpen,
    slug,
}: {
    showId: number;
    setWidgetOpen: (open: boolean) => void;
    slug?: string;
}) {
    const [step, setStep] = useSmartStorage<number>(LocalStorageKeys.STEP, 1);
    const { reservationId, reservationHash, setReservationUrlParams, clearReservationUrlParams } =
        useReservationUrlParams();
    const { data: show, isLoading, isError, error } = useGetShow(showId);
    const { t } = useTranslation();
    const { setMarketingEnabled, pageView } = useMarketing();

    const mainRef = useRef(null);
    const prevWidthRef = useRef(window.innerWidth);
    const lastPageViewStep = useRef<number | null>(null);

    // const langCount = Object.keys(LANGUAGE_OPTIONS).length;

    // Initialize marketing tracking when show data loads
    useEffect(() => {
        const trackingIds = show?.marketing?.tracking || {};
        const hasTrackingIds = Object.values(trackingIds).some((id) => id);
        const hasGlobalGa4Id = !!process.env.REACT_APP_GLOBAL_GA4_ID;

        if (hasTrackingIds || hasGlobalGa4Id) {
            console.log('[Fireknife] Initializing marketing tracking:', trackingIds);
            setMarketingEnabled(trackingIds);
        }
    }, [show, setMarketingEnabled]);

    useShowStyling(show);

    useEffect(() => {
        if (mainRef.current) {
            mainRef.current.scrollTop = 0;
        }
        if (lastPageViewStep.current !== step) {
            lastPageViewStep.current = step;
            pageView();
        }
    }, [step, pageView]);

    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            const crossedUp = prevWidthRef.current < 768 && newWidth >= 768;
            const crossedBreakpoint = crossedUp || (prevWidthRef.current >= 768 && newWidth < 768);

            if (crossedBreakpoint && mainRef.current) {
                // scroll top
                mainRef.current.scrollTop = 0;
            }

            if (crossedUp) {
                const aside = document.querySelector('.fireknife-step__aside');
                aside?.classList.remove('fireknife-step__aside--is-expanded');
            }

            prevWidthRef.current = newWidth;
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // if we have a proper site url, we should redirect the user to that with the extra show parameter
    useEffect(() => {
        if (!show) {
            return;
        }
        if (!isGenericFireknifeDomain()) {
            return;
        }
        if (show.venueSite === '') {
            return;
        }
        // redirect with ALL the current params
        const params = new URLSearchParams(window.location.search);
        params.set('fireknife', `${show.venueSlug}-${showId}`);

        // parse from show.venueSite
        const venueGetParams = new URLSearchParams(show.venueSite.split('?')[1]);
        // merge the params
        for (const [key, value] of venueGetParams.entries()) {
            params.set(key, value);
        }
        // construct the new URL
        const newUrl = `${show.venueSite.split('?')[0]}?${params.toString()}`;

        if (!newUrl.startsWith('http://') && !newUrl.startsWith('https://')) {
            // if the new URL is a valid URL, we redirect
            return;
        }

        // TODO: we might want to wait a bit before redirecting
        window.location.href = newUrl;
    }, [slug, showId, show]);

    if (isError) {
        return (
            <div style={{ padding: 16 }}>
                {t('errors.widgetLoadingError')}
                <br />
                <small>{error.message}</small>
            </div>
        );
    }

    if (isLoading || !show) {
        return <div className="fireknife-widget-loading"></div>;
    }

    // create a really primitive routing system here
    // 2 options currently:
    //      1. Booking flow (from 0)
    //      2. Booking summary (with modification and payment)
    //          - url contains query param for rid (reservation id) and rhash (hash)

    if (slug && slug !== show.venueSlug) {
        return <div style={{ padding: 16 }}>{t('errors.widgetSlugNotFound')}</div>;
    }

    const resetLocalStorage = () => {
        [
            LocalStorageKeys.STEP,
            LocalStorageKeys.CART,
            LocalStorageKeys.SEATS,
            LocalStorageKeys.TIMESLOT,
            LocalStorageKeys.GENERIC_BOOKING_FIELDS,
            LocalStorageKeys.COUPON,
        ].forEach((key) => {
            localStorage.removeItem(key);
        });

        window.location.reload();
    };

    return (
        <>
            <header className="fireknife-widget-header">
                <div className="fireknife-widget-header__box fireknife-widget-header__box--1">
                    {IS_DEVELOPMENT_ENV && (
                        <button onClick={resetLocalStorage}>Reset local storage</button>
                    )}
                    {/*{langCount === 2 ? (
                        <LanguagePickerSimple />
                    ) : langCount > 2 ? (
                        <LanguagePicker />
                    ) : (
                        '\u00A0'
                    )}*/}
                </div>
                <div className="fireknife-widget-header__box  fireknife-widget-header__box--2">
                    {typeof reservationId === 'number' && (
                        <TimeLimit
                            onExpire={() => {
                                clearReservationUrlParams();
                                setStep(1);
                            }}
                            reservationId={reservationId}
                            reservationHash={reservationHash}
                        />
                    )}
                    {!isGenericFireknifeDomain() && (
                        <button
                            className="fireknife-widget-header__btn-close fireknife-btn"
                            type="button"
                            aria-label={t('common.close')}
                            onClick={() => setWidgetOpen(false)}
                        >
                            <IconClose className="fireknife-widget-header__btn-close-icon" />
                        </button>
                    )}
                </div>
            </header>
            <main className="fireknife-widget-main" ref={mainRef}>
                {typeof reservationId !== 'number' && (
                    <CartContextProvider>
                        <StepRouter
                            step={step}
                            setStep={setStep}
                            show={show}
                            showId={showId}
                            setReservationUrlParams={setReservationUrlParams}
                        />
                    </CartContextProvider>
                )}
                {typeof reservationId === 'number' && (
                    <CartContextProvider>
                        <ReservationDetails
                            show={show}
                            reservationId={reservationId}
                            reservationHash={reservationHash}
                            clearReservationUrlParams={clearReservationUrlParams}
                        />
                    </CartContextProvider>
                )}
            </main>
        </>
    );
}
